import { ApolloError, useQuery } from '@apollo/client';
import { createContext, useContext, useMemo } from 'react';

import { Diagnosis, diagnoseTour } from './diagnostics';
import { mobileTourStatsQuery, mobileToursQuery, mobileTrackingShareQuery } from './graphql';
import {
  MobileTourStatsQuery,
  MobileTourStatsQueryVariables,
  MobileToursQuery,
  MobileToursQueryVariables,
  MobileTourPositionsFilter,
  MobileTourStatusGroup,
  MobileTrackingShareQuery,
  MobileTrackingShareQueryVariables,
} from '../lib/graphql';

export const MOBILE_FEATURE_FLAGS = [
  'enable_mobile_allocation',
  'allow_mobile_tracking_without_allocation',
  'enable_automatic_ttc_mobile_allocation',
  'enable_legacy_mobile_auth',
] as const;

export type MobileFeatureFlag = (typeof MOBILE_FEATURE_FLAGS)[number];

export const MOBILE_FLAG_LABEL: Record<MobileFeatureFlag, string> = {
  enable_mobile_allocation: 'Mobile allocation',
  allow_mobile_tracking_without_allocation: 'Tracking without allocation',
  enable_automatic_ttc_mobile_allocation: 'Automatic TTC allocation',
  enable_legacy_mobile_auth: 'Legacy credentials',
};

export const COMPANIES_PER_FLAG = 200;
export const STATS_WINDOW_DAYS = 30;

export const mobileFlags = (flags: readonly string[]): MobileFeatureFlag[] =>
  MOBILE_FEATURE_FLAGS.filter((flag) => flags.includes(flag));

export const TOURS_PAGE_SIZE = 50;

export interface MobileCompany {
  companyId: string;
  companyName: string;
  enabledFlags: MobileFeatureFlag[];
}

export type MobileTour = MobileToursQuery['mobileTours']['tours'][number];

export interface MobileTourRow {
  tour: MobileTour;
  diagnosis: Diagnosis;
}

export interface CompanyStats {
  mobile: number;
  tracked: number;
  completed: number;
  failed: number;
  ongoing: number;
  needsAction: number;
  withWarnings: number;
  withoutPositions: number;
}

export const EMPTY_STATS: CompanyStats = {
  mobile: 0,
  tracked: 0,
  completed: 0,
  failed: 0,
  ongoing: 0,
  needsAction: 0,
  withWarnings: 0,
  withoutPositions: 0,
};

export const sumStats = (all: CompanyStats[]): CompanyStats =>
  all.reduce<CompanyStats>(
    (acc, stats) => ({
      mobile: acc.mobile + stats.mobile,
      tracked: acc.tracked + stats.tracked,
      completed: acc.completed + stats.completed,
      failed: acc.failed + stats.failed,
      ongoing: acc.ongoing + stats.ongoing,
      needsAction: acc.needsAction + stats.needsAction,
      withWarnings: acc.withWarnings + stats.withWarnings,
      withoutPositions: acc.withoutPositions + stats.withoutPositions,
    }),
    { ...EMPTY_STATS },
  );

export interface MobileData {
  companies: MobileCompany[];
  statsByCompany: Map<string, CompanyStats>;
  totals: CompanyStats;
  dataAsOf: string | null;
  loading: boolean;
  error: ApolloError | undefined;
}

export const useMobileDataSource = (): MobileData => {
  const statsResult = useQuery<MobileTourStatsQuery, MobileTourStatsQueryVariables>(mobileTourStatsQuery, {
    variables: { days: STATS_WINDOW_DAYS },
  });

  const { companies, statsByCompany, totals } = useMemo(() => {
    const entries = statsResult.data?.mobileTourStats.companies ?? [];
    const byCompany = new Map<string, CompanyStats>();
    const rows: MobileCompany[] = [];
    entries.forEach((entry) => {
      if (entry.carrierId === null) {
        return;
      }
      byCompany.set(entry.carrierId, {
        mobile: entry.mobile,
        tracked: entry.tracked,
        completed: entry.completed,
        failed: entry.failed,
        ongoing: entry.ongoing,
        needsAction: entry.needsAction,
        withWarnings: entry.withWarnings,
        withoutPositions: entry.withoutPositions,
      });
      rows.push({
        companyId: entry.carrierId,
        companyName: entry.carrierName ?? entry.carrierId,
        enabledFlags: mobileFlags(entry.enabledFlags),
      });
    });
    return { companies: rows, statsByCompany: byCompany, totals: sumStats(entries) };
  }, [statsResult.data]);

  return {
    companies,
    statsByCompany,
    totals,
    dataAsOf: statsResult.data?.mobileTourStats.dataAsOf ?? null,
    loading: statsResult.loading,
    error: statsResult.error,
  };
};

export interface MobileToursFilter {
  carrierId: string | null;
  statusGroup: MobileTourStatusGroup | null;
  positions: MobileTourPositionsFilter | null;
  needsAction: boolean;
  search: string;
  page: number;
}

export const useMobileTours = ({ carrierId, statusGroup, positions, needsAction, search, page }: MobileToursFilter) => {
  const { data, loading, error } = useQuery<MobileToursQuery, MobileToursQueryVariables>(mobileToursQuery, {
    variables: {
      carrierId,
      statusGroup,
      positions,
      needsAction,
      days: STATS_WINDOW_DAYS,
      search: search === '' ? null : search,
      limit: TOURS_PAGE_SIZE,
      offset: page * TOURS_PAGE_SIZE,
    },
  });
  const rows = useMemo<MobileTourRow[]>(
    () => (data?.mobileTours.tours ?? []).map((tour) => ({ tour, diagnosis: diagnoseTour(tour) })),
    [data],
  );
  return {
    rows,
    count: data?.mobileTours.count ?? 0,
    dataAsOf: data?.mobileTours.dataAsOf ?? null,
    loading,
    error,
  };
};

export const useMobileTrackingShare = (carrierId: string | null) => {
  const { data, loading, error } = useQuery<MobileTrackingShareQuery, MobileTrackingShareQueryVariables>(
    mobileTrackingShareQuery,
    { variables: { days: STATS_WINDOW_DAYS, carrierId } },
  );
  return { points: data?.mobileTrackingShare.points ?? [], loading, error };
};

export const MobileDataContext = createContext<MobileData | null>(null);

export const useMobileData = (): MobileData => {
  const data = useContext(MobileDataContext);
  if (data === null) {
    throw new Error('useMobileData must be used inside MobileDataContext');
  }
  return data;
};
